import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { ClipboardList, ChevronRight, PenTool, X, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TYPE_LABEL = {
    pre: "Pré-formation",
    post_acquis: "Acquis pédagogiques",
    satisfaction_chaud: "Satisfaction à chaud",
};

function SignatureModal({ open, period, onClose, onSubmit }) {
    const sigRef = useRef(null);

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="signature-modal">
            <div className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-premium-lg">
                <button onClick={onClose} className="absolute right-5 top-5 text-brand-smoke hover:text-brand-ink" aria-label="Fermer">
                    <X className="h-5 w-5" />
                </button>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                    <PenTool className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-3xl">Émargement {period === "morning" ? "matin" : "après-midi"}</h3>
                <p className="mt-2 text-sm text-brand-smoke">
                    Signez dans la zone ci-dessous. Votre signature, l'horodatage et votre adresse IP seront archivés
                    avec la feuille d'émargement officielle.
                </p>
                <div className="mt-5 overflow-hidden rounded-2xl border border-brand-line bg-[#FAFAFA]">
                    <SignatureCanvas
                        ref={sigRef}
                        penColor="#0A0A0A"
                        backgroundColor="rgba(0,0,0,0)"
                        canvasProps={{
                            width: 460,
                            height: 200,
                            className: "w-full h-[200px]",
                            "data-testid": "signature-canvas",
                        }}
                    />
                </div>
                <div className="mt-3 flex items-center justify-between">
                    <button
                        onClick={() => sigRef.current?.clear()}
                        data-testid="signature-clear"
                        className="text-xs text-brand-smoke hover:text-brand-ink"
                    >
                        Effacer
                    </button>
                    <button
                        onClick={() => {
                            if (!sigRef.current || sigRef.current.isEmpty()) {
                                toast.error("Merci de signer avant de valider.");
                                return;
                            }
                            const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
                            onSubmit(dataUrl);
                        }}
                        data-testid="signature-submit"
                        className="rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c]"
                    >
                        Valider mon émargement
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function SessionQualiopiBlock({ sessionId, enrollmentId, sessionDay, sessionStartDate, emargement, onUpdate }) {
    const [evals, setEvals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [signOpen, setSignOpen] = useState(null); // "morning" | "afternoon" | null

    const today = new Date().toISOString().slice(0, 10);
    const sessionPast = sessionStartDate && sessionStartDate < today;
    const emargementIncomplete = !emargement?.morning_signed || !emargement?.afternoon_signed;
    const showEmargement = sessionDay || (sessionPast && emargementIncomplete);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/stagiaire/enrollments/${enrollmentId}/evaluations`);
            setEvals(data.evaluations);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (enrollmentId) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enrollmentId]);

    const submitSignature = async (dataUrl) => {
        try {
            await api.post(`/stagiaire/sessions/${sessionId}/emargement/sign`, {
                period: signOpen,
                signature: dataUrl,
            });
            toast.success(`Émargement ${signOpen === "morning" ? "matin" : "après-midi"} enregistré`);
            setSignOpen(null);
            onUpdate?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const pending = (evals || []).filter((e) => !e.submitted && e.unlocked);

    return (
        <section className="space-y-5" data-testid="session-qualiopi-block">
            {/* Evaluations bandeau */}
            <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="evaluations-banner">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                            <ClipboardList className="h-3.5 w-3.5" /> Évaluations Qualiopi
                        </div>
                        <h3 className="mt-2 font-display text-2xl">
                            {pending.length > 0 ? `${pending.length} évaluation${pending.length > 1 ? "s" : ""} à compléter` : "Toutes vos évaluations sont à jour"}
                        </h3>
                    </div>
                </div>
                {loading ? (
                    <div className="mt-4 text-sm text-brand-smoke">Chargement…</div>
                ) : (
                    <div className="mt-5 grid gap-3 md:grid-cols-3">
                        {(evals || []).map((e) => {
                            const actionable = e.unlocked && !e.submitted && !!e.template_id;
                            const cls = `group rounded-xl border p-4 transition ${
                                e.submitted
                                    ? "border-green-200 bg-green-50 cursor-default"
                                    : e.unlocked
                                        ? "border-brand-orange/30 bg-brand-orange/5 hover:border-brand-orange"
                                        : "border-brand-line bg-[#FAFAFA] opacity-60 cursor-not-allowed"
                            }`;
                            const inner = (
                                <>
                                    <div className="flex items-center justify-between">
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">
                                            {TYPE_LABEL[e.type]}
                                        </div>
                                        {e.submitted ? (
                                            <CheckCircle2 className="h-4 w-4 text-green-700" />
                                        ) : e.unlocked ? (
                                            <ChevronRight className="h-4 w-4 text-brand-orange transition-transform group-hover:translate-x-1" />
                                        ) : (
                                            <AlertCircle className="h-4 w-4 text-brand-smoke" />
                                        )}
                                    </div>
                                    <div className="mt-2 text-sm font-medium text-brand-ink">
                                        {e.submitted
                                            ? `Complété${e.score != null ? ` · ${e.score}%` : ""}`
                                            : e.unlocked
                                                ? "À compléter"
                                                : e.unlock_reason}
                                    </div>
                                </>
                            );
                            return actionable ? (
                                <Link
                                    key={e.template_id || e.type}
                                    to={`/stagiaire/evaluations/${e.template_id}?enrollment_id=${enrollmentId}`}
                                    data-testid={`eval-card-${e.type}`}
                                    className={cls}
                                >{inner}</Link>
                            ) : (
                                <div
                                    key={e.template_id || e.type}
                                    data-testid={`eval-card-${e.type}`}
                                    className={cls}
                                >{inner}</div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Émargement digital — visible le jour J ou en rattrapage si session passée non émargée */}
            {showEmargement && (
                <div className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium" data-testid="emargement-block">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                        <PenTool className="h-3.5 w-3.5" /> Émargement digital{sessionPast && " · rattrapage"}
                    </div>
                    <h3 className="mt-2 font-display text-2xl">
                        {sessionPast ? "Régularisez votre émargement" : "Signez votre présence"}
                    </h3>
                    <p className="mt-1 text-sm text-brand-smoke">
                        {sessionPast
                            ? "Cette session est passée et il manque une ou deux signatures. Régularisez-les pour débloquer votre attestation."
                            : "À compléter en début de matinée et au retour de pause déjeuner. Document conforme Qualiopi via Centre Axiome."}
                    </p>
                    <div className="mt-5 grid gap-3 md:grid-cols-2">
                        {(["morning", "afternoon"]).map((period) => {
                            const signed = period === "morning" ? emargement?.morning_signed : emargement?.afternoon_signed;
                            return (
                                <button
                                    key={period}
                                    onClick={() => !signed && setSignOpen(period)}
                                    disabled={signed}
                                    data-testid={`emargement-${period}-btn`}
                                    className={`rounded-xl border p-5 text-left transition ${
                                        signed
                                            ? "border-green-200 bg-green-50 cursor-default"
                                            : "border-brand-orange/30 bg-brand-orange/5 hover:border-brand-orange"
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                                            {period === "morning" ? "Matin (9h–13h)" : "Après-midi (14h–17h)"}
                                        </div>
                                        {signed ? (
                                            <CheckCircle2 className="h-4 w-4 text-green-700" />
                                        ) : (
                                            <PenTool className="h-4 w-4 text-brand-orange" />
                                        )}
                                    </div>
                                    <div className="mt-2 text-sm font-medium text-brand-ink">
                                        {signed ? "✓ Émargé" : "Cliquez pour signer"}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <SignatureModal
                open={!!signOpen}
                period={signOpen}
                onClose={() => setSignOpen(null)}
                onSubmit={submitSignature}
            />
        </section>
    );
}
